/**
 * Members — directorio de asociados de la organizacion.
 *
 * Conectada al backend /api/management/members (F2). Cada submodulo del
 * bloque Gestion (Asociados/Cuotas/Presupuesto) se activa por organizacion
 * via `config.management.members.enabled`. Si esta deshabilitado el sidebar
 * lo oculta y este componente muestra una pantalla informativa.
 *
 * Decisiones en DOC/ES/MODULO_ASOCIADOS_DECISIONES.md.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  EnvelopeIcon,
  PhoneIcon,
  IdentificationIcon,
  XMarkIcon,
  CheckBadgeIcon,
  PencilSquareIcon,
  UserCircleIcon,
  TrashIcon,
  NoSymbolIcon,
  CheckCircleIcon,
  ClockIcon,
  MinusCircleIcon,
} from '@heroicons/react/24/outline';
import { Header } from '@/components/Layout';
import {
  membersApi,
  feesApi,
  type Member,
  type MemberStatus,
  type MemberStats,
  type MemberFeeStatusRow,
  type MemberFee,
} from '@/services/api';
import { useManagementFeature } from '@/hooks/useManagementFeature';
import { useAuthStore } from '@/stores/auth';
import { PERMISSIONS } from '@/types';
import toast from 'react-hot-toast';

const STATUS_OPTIONS: Array<{ value: MemberStatus | 'all'; label: string }> = [
  { value: 'all', label: 'Todos' },
  { value: 'active', label: 'Activos' },
  { value: 'pending', label: 'Pendientes' },
  { value: 'inactive', label: 'Bajas' },
];

function StatusBadge({ s }: { s: MemberStatus }) {
  const map: Record<MemberStatus, { bg: string; label: string }> = {
    active: { bg: 'bg-emerald-100 text-emerald-700', label: 'Activo' },
    pending: { bg: 'bg-amber-100 text-amber-700', label: 'Pendiente' },
    inactive: { bg: 'bg-gray-200 text-gray-700', label: 'Baja' },
  };
  const m = map[s];
  return <span className={`text-xs px-2 py-0.5 rounded-full ${m.bg}`}>{m.label}</span>;
}

export default function Members() {
  const enabled = useManagementFeature('members');
  const { hasPermission } = useAuthStore();
  const canManage = hasPermission(PERMISSIONS.MEMBERS_MANAGE);

  // Estado pantalla bloqueada — sin llamar a la API.
  if (!enabled) return <ModuleDisabled />;

  return <MembersInner canManage={canManage} />;
}

function MembersInner({ canManage }: { canManage: boolean }) {
  const [members, setMembers] = useState<Member[]>([]);
  const [stats, setStats] = useState<MemberStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<MemberStatus | 'all'>('all');
  const [selected, setSelected] = useState<Member | null>(null);
  const [editing, setEditing] = useState<Member | 'new' | null>(null);

  // Debounce del buscador para no martillear la API en cada tecla.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query), 250);
    return () => clearTimeout(t);
  }, [query]);

  const loadStats = useCallback(async () => {
    try {
      const { data } = await membersApi.stats();
      setStats(data);
    } catch {
      // no critico
    }
  }, []);

  const loadMembers = useCallback(async () => {
    setLoading(true);
    try {
      const params: Parameters<typeof membersApi.list>[0] = { limit: 200 };
      if (statusFilter !== 'all') params.status = statusFilter;
      if (debouncedQuery.trim()) params.search = debouncedQuery.trim();
      const { data } = await membersApi.list(params);
      setMembers(data.members);
    } catch (err: unknown) {
      const e = err as { response?: { status?: number; data?: { error?: string } } };
      if (e.response?.status !== 403) {
        toast.error(e.response?.data?.error || 'Error cargando asociados');
      }
    } finally {
      setLoading(false);
    }
  }, [statusFilter, debouncedQuery]);

  useEffect(() => {
    loadMembers();
  }, [loadMembers]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const handleDelete = async (m: Member) => {
    if (!window.confirm(`¿Eliminar a ${m.fullName}? Esta acción no se puede deshacer.`)) return;
    try {
      await membersApi.remove(m._id);
      toast.success('Asociado eliminado');
      setSelected(null);
      loadMembers();
      loadStats();
    } catch (err) {
      const e = err as { response?: { data?: { error?: string } } };
      toast.error(e.response?.data?.error || 'No se pudo eliminar');
    }
  };

  const handleSaved = () => {
    setEditing(null);
    loadMembers();
    loadStats();
  };

  return (
    <div>
      <Header
        title="Asociados"
        subtitle="Directorio de miembros de la organización"
        actions={
          canManage ? (
            <button
              onClick={() => setEditing('new')}
              className="inline-flex items-center gap-2 px-3 py-1.5 bg-vida-600 text-white text-sm font-medium rounded-lg hover:bg-vida-700"
            >
              <PlusIcon className="h-4 w-4" /> Nuevo asociado
            </button>
          ) : null
        }
      />

      <div className="p-6 space-y-4">
        {/* Resumen */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div className="stat-card"><p className="stat-card-title">Total</p><p className="stat-card-value">{stats?.total ?? '—'}</p></div>
          <div className="stat-card"><p className="stat-card-title">Activos</p><p className="stat-card-value text-emerald-700">{stats?.active ?? '—'}</p></div>
          <div className="stat-card"><p className="stat-card-title">Pendientes</p><p className="stat-card-value text-amber-700">{stats?.pending ?? '—'}</p></div>
          <div className="stat-card"><p className="stat-card-title">Bajas</p><p className="stat-card-value text-gray-500">{stats?.inactive ?? '—'}</p></div>
          <div className="stat-card"><p className="stat-card-title">Con dashboard</p><p className="stat-card-value text-vida-700">{stats?.withDashboard ?? '—'}</p></div>
        </div>

        {/* Buscador + filtros */}
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[240px]">
            <MagnifyingGlassIcon className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar por nombre, NIF, email o teléfono…"
              className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-gray-300 focus:ring-vida-500 focus:border-vida-500"
            />
          </div>
          {STATUS_OPTIONS.map((s) => (
            <button
              key={s.value}
              onClick={() => setStatusFilter(s.value)}
              className={`px-3 py-1.5 text-sm rounded-lg border ${
                statusFilter === s.value
                  ? 'border-vida-600 bg-vida-50 text-vida-700'
                  : 'border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>

        {/* Lista */}
        <section className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase text-gray-500">
              <tr>
                <th className="text-left px-4 py-2">Nombre</th>
                <th className="text-left px-4 py-2 hidden md:table-cell">NIF</th>
                <th className="text-left px-4 py-2">Email</th>
                <th className="text-left px-4 py-2 hidden md:table-cell">Teléfono</th>
                <th className="text-left px-4 py-2">Estado</th>
                <th className="text-left px-4 py-2 hidden lg:table-cell">Cuenta</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading && (
                <tr>
                  <td colSpan={6} className="px-4 py-6 text-center text-sm text-gray-500">
                    Cargando…
                  </td>
                </tr>
              )}
              {!loading && members.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-6 text-center text-sm text-gray-500">
                    {debouncedQuery || statusFilter !== 'all'
                      ? 'Sin resultados para la búsqueda.'
                      : 'Aún no hay asociados. Crea el primero con "Nuevo asociado".'}
                  </td>
                </tr>
              )}
              {!loading &&
                members.map((m) => (
                  <tr
                    key={m._id}
                    onClick={() => setSelected(m)}
                    className={`cursor-pointer hover:bg-gray-50 ${
                      selected?._id === m._id ? 'bg-vida-50/40' : ''
                    }`}
                  >
                    <td className="px-4 py-2 font-medium text-gray-900">{m.fullName}</td>
                    <td className="px-4 py-2 text-gray-600 hidden md:table-cell font-mono text-xs">
                      {m.nif || '—'}
                    </td>
                    <td className="px-4 py-2 text-gray-600">{m.email || '—'}</td>
                    <td className="px-4 py-2 text-gray-600 hidden md:table-cell">{m.phone || '—'}</td>
                    <td className="px-4 py-2">
                      <StatusBadge s={m.status} />
                    </td>
                    <td className="px-4 py-2 hidden lg:table-cell">
                      {m.user_id ? (
                        <span className="inline-flex items-center gap-1 text-xs text-vida-700">
                          <CheckBadgeIcon className="h-3.5 w-3.5" /> Vinculado
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </section>
      </div>

      {selected && (
        <MemberDetail
          member={selected}
          canManage={canManage}
          onClose={() => setSelected(null)}
          onEdit={() => setEditing(selected)}
          onDelete={() => handleDelete(selected)}
        />
      )}
      {editing !== null && (
        <MemberEditor
          initial={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
}

// ----- Modulo deshabilitado -----

function ModuleDisabled() {
  return (
    <div>
      <Header title="Asociados" subtitle="Directorio de miembros de la organización" />
      <div className="p-6">
        <div className="max-w-xl mx-auto rounded-xl border border-gray-200 bg-white p-8 text-center">
          <NoSymbolIcon className="h-12 w-12 mx-auto text-gray-300" />
          <h2 className="mt-3 text-base font-semibold text-gray-900">
            Módulo de Asociados no habilitado
          </h2>
          <p className="mt-2 text-sm text-gray-500">
            Esta organización no tiene activado el módulo de gestión de asociados. Un
            administrador puede habilitarlo en la configuración de la organización
            (campo <code className="text-xs">config.management.members.enabled</code>).
          </p>
        </div>
      </div>
    </div>
  );
}

// ----- Ficha lateral -----

function MemberDetail({
  member,
  canManage,
  onClose,
  onEdit,
  onDelete,
}: {
  member: Member;
  canManage: boolean;
  onClose: () => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const initials = useMemo(
    () =>
      member.fullName
        .split(' ')
        .map((s) => s[0])
        .slice(0, 2)
        .join('')
        .toUpperCase(),
    [member.fullName]
  );

  return (
    <div className="fixed inset-0 z-40 flex" onClick={onClose}>
      <div className="flex-1 bg-black/30" />
      <div
        className="w-full max-w-md bg-white shadow-xl overflow-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-base font-semibold text-gray-900">Ficha del asociado</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
        <div className="p-4 space-y-5">
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 rounded-full bg-vida-100 text-vida-700 flex items-center justify-center font-semibold">
              {initials}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-lg font-semibold text-gray-900">{member.fullName}</p>
              {member.joinedAt && (
                <p className="text-xs text-gray-500">
                  Alta{' '}
                  {new Date(member.joinedAt).toLocaleDateString('es-ES', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                  })}
                </p>
              )}
              <div className="mt-1">
                <StatusBadge s={member.status} />
              </div>
            </div>
            {canManage && (
              <button
                title="Editar"
                onClick={onEdit}
                className="text-gray-400 hover:text-vida-700"
              >
                <PencilSquareIcon className="h-5 w-5" />
              </button>
            )}
          </div>

          <section>
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              Datos personales
            </h3>
            <ul className="space-y-2 text-sm">
              {member.nif && (
                <li className="flex items-center gap-2">
                  <IdentificationIcon className="h-4 w-4 text-gray-400" />
                  <span className="font-mono">{member.nif}</span>
                </li>
              )}
              {member.email && (
                <li className="flex items-center gap-2">
                  <EnvelopeIcon className="h-4 w-4 text-gray-400" />
                  <a href={`mailto:${member.email}`} className="text-vida-700 hover:underline">
                    {member.email}
                  </a>
                </li>
              )}
              {member.phone && (
                <li className="flex items-center gap-2">
                  <PhoneIcon className="h-4 w-4 text-gray-400" />
                  <a href={`tel:${member.phone}`} className="text-vida-700 hover:underline">
                    {member.phone}
                  </a>
                </li>
              )}
              {member.address && <li className="text-gray-600">{member.address}</li>}
              {!member.nif && !member.email && !member.phone && !member.address && (
                <li className="text-xs text-gray-400">Sin datos de contacto.</li>
              )}
            </ul>
          </section>

          <section>
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              Cuotas y domiciliación
            </h3>
            <p className="text-sm text-gray-600">
              {member.iban ? `IBAN ${member.iban}` : 'Sin domiciliación bancaria.'}
            </p>
            <MemberFeesHistory memberId={member._id} />
          </section>

          <section>
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              Cuenta en el dashboard
            </h3>
            {member.user_id ? (
              <div className="flex items-center gap-2 text-sm">
                <UserCircleIcon className="h-5 w-5 text-vida-600" />
                <span>Vinculado con un usuario del dashboard.</span>
              </div>
            ) : (
              <p className="text-xs text-gray-500">
                El asociado no tiene cuenta vinculada en el dashboard. La invitación/vinculación
                se implementará más adelante.
              </p>
            )}
          </section>

          {member.notes && (
            <section>
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                Notas
              </h3>
              <p className="text-sm text-gray-600 italic whitespace-pre-line">{member.notes}</p>
            </section>
          )}

          {canManage && (
            <section className="flex flex-wrap gap-2 pt-2 border-t border-gray-100">
              <button
                onClick={onEdit}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                <PencilSquareIcon className="h-4 w-4" /> Editar
              </button>
              <button
                onClick={onDelete}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-white border border-red-200 text-red-700 rounded-lg hover:bg-red-50"
              >
                <TrashIcon className="h-4 w-4" /> Eliminar
              </button>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}

// ----- Editor (crear / editar) -----

function MemberEditor({
  initial,
  onClose,
  onSaved,
}: {
  initial: Member | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = !!initial;
  const [form, setForm] = useState<Partial<Member>>({
    fullName: initial?.fullName || '',
    nif: initial?.nif || '',
    email: initial?.email || '',
    phone: initial?.phone || '',
    address: initial?.address || '',
    iban: initial?.iban || '',
    notes: initial?.notes || '',
    status: initial?.status || 'pending',
    joinedAt: initial?.joinedAt ? initial.joinedAt.slice(0, 10) : '',
  });
  const [saving, setSaving] = useState(false);

  const handleChange =
    <K extends keyof Member>(field: K) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
      setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.fullName || !form.fullName.trim()) {
      toast.error('El nombre es obligatorio');
      return;
    }
    setSaving(true);
    try {
      const payload: Partial<Member> = { ...form };
      if (payload.joinedAt === '') delete payload.joinedAt;
      if (payload.email === '') delete payload.email;
      if (isEdit && initial) {
        await membersApi.update(initial._id, payload);
        toast.success('Asociado actualizado');
      } else {
        await membersApi.create(payload);
        toast.success('Asociado creado');
      }
      onSaved();
    } catch (err) {
      const e = err as { response?: { data?: { error?: string } } };
      toast.error(e.response?.data?.error || 'No se pudo guardar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex" onClick={onClose}>
      <div className="flex-1 bg-black/30" />
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-md bg-white shadow-xl overflow-auto flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-base font-semibold text-gray-900">
            {isEdit ? 'Editar asociado' : 'Nuevo asociado'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-700"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="p-4 space-y-3 flex-1">
          <Field label="Nombre completo" required>
            <input
              value={form.fullName || ''}
              onChange={handleChange('fullName')}
              className="form-input"
              required
              autoFocus
            />
          </Field>
          <Field label="NIF">
            <input value={form.nif || ''} onChange={handleChange('nif')} className="form-input" />
          </Field>
          <Field label="Email">
            <input
              type="email"
              value={form.email || ''}
              onChange={handleChange('email')}
              className="form-input"
            />
          </Field>
          <Field label="Teléfono">
            <input
              value={form.phone || ''}
              onChange={handleChange('phone')}
              className="form-input"
            />
          </Field>
          <Field label="Dirección">
            <input
              value={form.address || ''}
              onChange={handleChange('address')}
              className="form-input"
            />
          </Field>
          <Field label="IBAN">
            <input
              value={form.iban || ''}
              onChange={handleChange('iban')}
              className="form-input"
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Estado">
              <select
                value={form.status || 'pending'}
                onChange={handleChange('status')}
                className="form-input"
              >
                <option value="pending">Pendiente</option>
                <option value="active">Activo</option>
                <option value="inactive">Baja</option>
              </select>
            </Field>
            <Field label="Fecha de alta">
              <input
                type="date"
                value={form.joinedAt || ''}
                onChange={handleChange('joinedAt')}
                className="form-input"
              />
            </Field>
          </div>
          <Field label="Notas">
            <textarea
              rows={3}
              value={form.notes || ''}
              onChange={handleChange('notes')}
              className="form-input"
            />
          </Field>
        </div>

        <div className="p-4 border-t border-gray-100 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 rounded-lg"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-4 py-1.5 text-sm bg-vida-600 text-white rounded-lg hover:bg-vida-700 disabled:opacity-50"
          >
            {saving ? 'Guardando…' : isEdit ? 'Guardar' : 'Crear'}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-gray-700">
        {label}
        {required && <span className="text-red-600 ml-0.5">*</span>}
      </span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

// ----- Historico de cuotas del asociado -----

function MemberFeesHistory({ memberId }: { memberId: string }) {
  // Solo intentamos cargar el historial si el modulo Cuotas esta activo.
  // Asi evitamos pintar el bloque y disparar requests que devuelvan 403.
  const feesEnabled = useManagementFeature('fees');
  const [statuses, setStatuses] = useState<MemberFeeStatusRow[] | null>(null);
  const [plans, setPlans] = useState<MemberFee[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!feesEnabled) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    Promise.all([
      feesApi.listStatuses({ memberId, limit: 8 }),
      feesApi.listPlans(),
    ])
      .then(([sRes, pRes]) => {
        if (cancelled) return;
        setStatuses(sRes.data.statuses);
        setPlans(pRes.data.fees);
      })
      .catch(() => {
        if (!cancelled) setStatuses([]);
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [memberId, feesEnabled]);

  if (!feesEnabled) return null;
  if (loading) {
    return <p className="mt-3 text-xs text-gray-400">Cargando histórico…</p>;
  }
  if (!statuses || statuses.length === 0) {
    return (
      <p className="mt-3 text-xs text-gray-400">
        Sin cobros registrados para este asociado.
      </p>
    );
  }

  const planLabel = (id: string) =>
    plans.find((p) => p._id === id)?.label || 'Plan';

  return (
    <ul className="mt-3 space-y-1.5">
      {statuses.map((s) => {
        const Icon =
          s.status === 'paid' ? CheckCircleIcon : s.status === 'pending' ? ClockIcon : MinusCircleIcon;
        const iconClass =
          s.status === 'paid'
            ? 'text-emerald-600'
            : s.status === 'pending'
              ? 'text-amber-600'
              : 'text-gray-400';
        return (
          <li
            key={s._id}
            className="flex items-center justify-between text-xs bg-gray-50 rounded px-2 py-1.5"
          >
            <span className="flex items-center gap-1.5 text-gray-700">
              <Icon className={`h-3.5 w-3.5 ${iconClass}`} />
              <span>
                <strong className="font-semibold text-gray-900">{s.period}</strong>{' '}
                · {planLabel(s.memberFeeId)}
              </span>
            </span>
            <span className="font-mono text-gray-600">
              {(s.amount / 100).toLocaleString('es-ES', {
                style: 'currency',
                currency: 'EUR',
                maximumFractionDigits: 2,
              })}
            </span>
          </li>
        );
      })}
    </ul>
  );
}
